/**
 * Moderation-action gating — pure predicates per PLAYER_MODEL §10 +
 * CLIENT_DESIGN §14.5.
 *
 * Rules (local-client enforcement; the server re-checks authoritatively
 * and may still reject with an `Error::Forbidden`):
 *   - `canKick`:
 *       * hidden unless viewer is Moderator or Owner
 *       * disabled if target is self
 *       * disabled if target role ≥ viewer role (no peer/superior kicks)
 *   - `canPromote` (Player → Moderator):
 *       * Owner only
 *       * disabled if target is self or already Moderator+
 *   - `canDemote` (Moderator → Player):
 *       * Owner only
 *       * disabled if target is Player (nothing to demote to)
 *       * disabled if target is Owner (separate "demote-last-Owner"
 *         check is enforced server-side against configured tokens per
 *         decision 23.1-#5; the client does not prevent Owner→demote
 *         attempts pre-emptively — it shows the Forbidden error toast
 *         when the server rejects)
 */

import type { Role } from '../wire';

export interface ModerationContext {
  viewerRole: Role;
  viewerIsSelf: boolean; // target === viewer
  targetRole: Role;
}

function rank(role: Role): number {
  switch (role) {
    case 'Player':
      return 0;
    case 'Moderator':
      return 1;
    case 'Owner':
      return 2;
  }
}

export function canKick(ctx: ModerationContext): boolean {
  if (ctx.viewerIsSelf) return false;
  if (ctx.viewerRole !== 'Moderator' && ctx.viewerRole !== 'Owner') return false;
  // target role strictly less than viewer role (moderators can't kick
  // other moderators; owners can kick moderators + players).
  return rank(ctx.targetRole) < rank(ctx.viewerRole);
}

export function canPromote(ctx: ModerationContext): boolean {
  if (ctx.viewerIsSelf) return false;
  if (ctx.viewerRole !== 'Owner') return false;
  return ctx.targetRole === 'Player';
}

export function canDemote(ctx: ModerationContext): boolean {
  if (ctx.viewerIsSelf) return false;
  if (ctx.viewerRole !== 'Owner') return false;
  return ctx.targetRole === 'Moderator';
}

/** True if any of the three actions are visible — used to hide the
 *  moderation section entirely when the viewer has no gated buttons. */
export function hasAnyModerationAction(ctx: ModerationContext): boolean {
  return canKick(ctx) || canPromote(ctx) || canDemote(ctx);
}
